import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { RankBadge } from '../components/RankBadge';
import { formatAddress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
    Calendar,
    Trophy,
    Crown,
    Medal,
    Star,
    Users,
    Clock,
    Target,
    ChevronLeft,
    Zap,
    Award,
    TrendingUp,
    Flame,
    Share2,
    Twitter
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Season Status Badge
function SeasonStatusBadge({ status }) {
    const config = {
        active: { color: '#00FF66', label: 'Active', icon: Flame },
        upcoming: { color: '#FFD700', label: 'Upcoming', icon: Clock },
        completed: { color: '#9B6FE8', label: 'Completed', icon: Trophy },
        cancelled: { color: '#FF6B6B', label: 'Cancelled', icon: null }
    };
    
    const { color, label, icon: Icon } = config[status] || config.upcoming;
    
    return (
        <Badge style={{ backgroundColor: `${color}20`, color }} className="text-sm px-3 py-1">
            {Icon && <Icon className="w-4 h-4 mr-1" />}
            {label}
        </Badge>
    );
}

// Leaderboard Row
function LeaderboardRow({ entry, rank, currentAddress }) {
    const isCurrentUser = entry.address === currentAddress?.toLowerCase();
    
    return (
        <div 
            className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                isCurrentUser 
                    ? 'bg-[#00FF66]/10 border border-[#00FF66]/30' 
                    : 'bg-white/[0.02] hover:bg-white/[0.04]'
            }`}
            data-testid={`leaderboard-row-${rank}`}
        >
            <div className="flex items-center gap-4">
                {/* Rank */}
                <div className="w-12 h-12 flex items-center justify-center">
                    {rank <= 3 ? <RankBadge rank={rank} size="lg" /> : (
                        <span className="font-mono text-white/50 text-lg">#{rank}</span>
                    )}
                </div>
                
                {/* Address */}
                <div>
                    <Link 
                        to={`/profile/${entry.address}`}
                        className="text-white font-mono hover:text-[#00FF66] transition-colors"
                    >
                        {formatAddress(entry.address)}
                    </Link>
                    {isCurrentUser && (
                        <Badge className="ml-2 bg-[#00FF66]/20 text-[#00FF66] text-xs">You</Badge>
                    )}
                </div>
            </div>
            
            {/* Points */}
            <div className="text-right">
                <div className={`text-2xl font-bold ${
                    rank === 1 ? 'text-[#FFD700]' :
                    rank === 2 ? 'text-gray-300' :
                    rank === 3 ? 'text-[#CD7F32]' :
                    'text-white'
                }`}>
                    {entry.points}
                </div>
                <div className="text-white/40 text-sm">points</div>
            </div>
        </div>
    );
}

// Tournament Card in Season
function SeasonTournamentCard({ tournament }) {
    const statusColors = {
        active: 'bg-[#00FF66]/20 text-[#00FF66]',
        upcoming: 'bg-[#FFD700]/20 text-[#FFD700]',
        completed: 'bg-[#9B6FE8]/20 text-[#9B6FE8]'
    };
    
    return (
        <Link to={`/tournaments`}>
            <Card className="bg-white/[0.02] border-white/5 hover:border-[#9B6FE8]/30 transition-all cursor-pointer">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-semibold">{tournament.name}</h4>
                        <Badge className={statusColors[tournament.status] || statusColors.upcoming}>
                            {tournament.status}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-white/40">
                        <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {tournament.participants?.length || 0} participants
                        </span>
                        <span className="flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            {tournament.prize_pool || 0} ETH
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

// Twitter Share Button
function ShareOnTwitter({ season, userRank }) {
    const handleShare = () => {
        const text = userRank 
            ? `I'm ranked #${userRank} in the "${season.name}" season on Bet4Peer`
            : `Check out the "${season.name}" season on Bet4Peer - The blockchain P2P betting platform`;
        
        const url = window.location.href;
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        
        window.open(twitterUrl, '_blank', 'width=550,height=420');
    };
    
    return (
        <Button 
            variant="outline" 
            size="sm" 
            className="border-[#1DA1F2]/30 text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
            onClick={handleShare}
            data-testid="share-twitter-button"
        >
            <Twitter className="w-4 h-4 mr-2" />
            Share on X
        </Button>
    );
}

export function SeasonDetailPage() {
    const { seasonId } = useParams();
    const { address, isConnected } = useWeb3();
    const [season, setSeason] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pointsHistory, setPointsHistory] = useState([]);
    
    useEffect(() => {
        if (seasonId) {
            fetchSeasonDetails();
        }
    }, [seasonId]);
    
    const fetchSeasonDetails = async () => {
        setLoading(true);
        try {
            // Fetch season with leaderboard
            const seasonRes = await fetch(`${API_URL}/api/seasons/${seasonId}`);
            if (seasonRes.ok) {
                const seasonData = await seasonRes.json();
                setSeason(seasonData);
                
                // Fetch tournaments in this season
                if (seasonData.tournaments && seasonData.tournaments.length > 0) {
                    const tournamentsData = await Promise.all(
                        seasonData.tournaments.map(async (tid) => {
                            try {
                                const res = await fetch(`${API_URL}/api/tournaments/${tid}`);
                                if (res.ok) return await res.json();
                            } catch (tourErr) {
                                console.warn(`Tournament ${tid} fetch failed:`, tourErr?.message);
                            }
                            return null;
                        })
                    );
                    setTournaments(tournamentsData.filter(Boolean));
                }
                
                // Generate mock points history for chart
                const leaderboard = seasonData.leaderboard || [];
                if (leaderboard.length > 0) {
                    const mockHistory = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, idx) => ({
                        week,
                        ...Object.fromEntries(
                            leaderboard.slice(0, 5).map((entry, i) => [
                                `player${i + 1}`,
                                Math.floor(entry.points * (0.25 + idx * 0.25))
                            ])
                        )
                    }));
                    setPointsHistory(mockHistory);
                }
            } else {
                toast.error('Season not found');
            }
        } catch (error) {
            console.error('Failed to fetch season:', error);
            toast.error('Failed to load season details');
        } finally {
            setLoading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Skeleton className="h-10 w-40 mb-6 bg-white/5" />
                <Skeleton className="h-64 w-full mb-6 bg-white/5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-96 lg:col-span-2 bg-white/5" />
                    <Skeleton className="h-96 bg-white/5" />
                </div>
            </div>
        );
    }
    
    if (!season) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
                <h1 className="text-2xl font-bold text-white mb-4">Season Not Found</h1>
                <Link to="/seasons">
                    <Button variant="outline">Back to Seasons</Button>
                </Link>
            </div>
        );
    }
    
    const startDate = new Date(season.start_date);
    const endDate = new Date(season.end_date);
    const now = new Date();
    const totalDays = differenceInDays(endDate, startDate);
    const daysElapsed = differenceInDays(now, startDate);
    const progress = season.status === 'completed' ? 100 : Math.max(0, Math.min(100, (daysElapsed / totalDays) * 100));
    
    const userEntry = season.leaderboard?.find(e => e.address === address?.toLowerCase());
    const userRank = userEntry?.rank;
    
    const COLORS = ['#FFD700', '#00FF66', '#9B6FE8', '#FF6B6B', '#4ECDC4'];
    
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="season-detail-page">
            {/* Back Button */}
            <Link to="/seasons" className="inline-flex items-center text-white/50 hover:text-white mb-6 transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Seasons
            </Link>
            
            {/* Header Card */}
            <Card className="bg-gradient-to-r from-[#FFD700]/10 to-[#9B6FE8]/10 border-[#FFD700]/30 mb-8">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFD700]/30 to-[#FF6B6B]/30 flex items-center justify-center">
                                <Calendar className="w-8 h-8 text-[#FFD700]" />
                            </div>
                            <div>
                                <SeasonStatusBadge status={season.status} />
                                <h1 className="text-2xl md:text-3xl font-bold text-white mt-2">{season.name}</h1>
                                <p className="text-white/50 mt-1">{season.description}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <ShareOnTwitter season={season} userRank={userRank} />
                            {season.on_chain_id && (
                                <Badge className="bg-[#FFD700]/20 text-[#FFD700]">
                                    <Zap className="w-3 h-3 mr-1" />
                                    On-chain #{season.on_chain_id}
                                </Badge>
                            )}
                        </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-6">
                        <div className="flex justify-between text-sm text-white/50 mb-2">
                            <span>{format(startDate, 'MMM d, yyyy')}</span>
                            <span>{Math.round(progress)}% complete</span>
                            <span>{format(endDate, 'MMM d, yyyy')}</span>
                        </div>
                        <Progress value={progress} className="h-2 bg-white/10" />
                    </div>
                    
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                        <div className="text-center p-3 rounded-xl bg-white/5">
                            <Trophy className="w-6 h-6 text-[#FFD700] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white">{season.tournaments?.length || 0}</div>
                            <div className="text-xs text-white/40">Tournaments</div>
                        </div>
                        <div className="text-center p-3 rounded-xl bg-white/5">
                            <Users className="w-6 h-6 text-[#00FF66] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white">{season.leaderboard?.length || 0}</div>
                            <div className="text-xs text-white/40">Competitors</div>
                        </div>
                        <div className="text-center p-3 rounded-xl bg-white/5">
                            <Star className="w-6 h-6 text-[#9B6FE8] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white">{season.prize_pool || 0}</div>
                            <div className="text-xs text-white/40">ETH Prizes</div>
                        </div>
                        <div className="text-center p-3 rounded-xl bg-white/5">
                            <Target className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white">{season.leaderboard?.[0]?.points || 0}</div>
                            <div className="text-xs text-white/40">Top Score</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Your Position Card (if connected) */}
            {userEntry && (
                <Card className="bg-[#00FF66]/10 border-[#00FF66]/30 mb-8">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl bg-[#00FF66]/20 flex items-center justify-center">
                                    <Medal className="w-7 h-7 text-[#00FF66]" />
                                </div>
                                <div>
                                    <div className="text-white/50 text-sm">Your Current Position</div>
                                    <div className="text-3xl font-bold text-[#00FF66]">#{userRank}</div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-white/50 text-sm">Your Points</div>
                                <div className="text-3xl font-bold text-white">{userEntry.points}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Leaderboard */}
                <div className="lg:col-span-2">
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-xl text-white flex items-center gap-2">
                                <Award className="w-6 h-6 text-[#FFD700]" />
                                Season Leaderboard
                            </CardTitle>
                            <CardDescription className="text-white/50">
                                Top performers ranked by total points earned
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {!season.leaderboard || season.leaderboard.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                    <p className="text-white/50">No rankings yet</p>
                                    <p className="text-white/30 text-sm">Compete in tournaments to earn points!</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {season.leaderboard.map((entry, idx) => (
                                        <LeaderboardRow 
                                            key={entry.address}
                                            entry={entry}
                                            rank={idx + 1}
                                            currentAddress={address}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    
                    {/* Points Evolution Chart */}
                    {pointsHistory.length > 0 && (
                        <Card className="bg-white/[0.02] border-white/5 mt-6">
                            <CardHeader>
                                <CardTitle className="text-lg text-white flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                                    Points Evolution
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={pointsHistory}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                                        <XAxis dataKey="week" stroke="#ffffff50" fontSize={12} />
                                        <YAxis stroke="#ffffff50" fontSize={12} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#1A0B2E', 
                                                border: '1px solid #ffffff20',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Line 
                                                key={i}
                                                type="monotone" 
                                                dataKey={`player${i}`} 
                                                stroke={COLORS[i - 1]} 
                                                strokeWidth={2}
                                                dot={{ fill: COLORS[i - 1], strokeWidth: 2 }}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}
                </div>
                
                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Point System */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Target className="w-5 h-5 text-[#FFD700]" />
                                Point System
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {Object.entries(season.point_multipliers || {}).map(([place, pts]) => {
                                    const labels = {
                                        first: '1st Place',
                                        second: '2nd Place',
                                        third: '3rd Place',
                                        top_10: 'Top 10',
                                        participant: 'Participant'
                                    };
                                    const colors = {
                                        first: '#FFD700',
                                        second: '#C0C0C0',
                                        third: '#CD7F32',
                                        top_10: '#9B6FE8',
                                        participant: '#00FF66'
                                    };
                                    return (
                                        <div key={place} className="flex items-center justify-between">
                                            <span className="text-white/60 text-sm">{labels[place] || place}</span>
                                            <Badge style={{ backgroundColor: `${colors[place]}20`, color: colors[place] }}>
                                                +{pts} pts
                                            </Badge>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Season Tournaments */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Trophy className="w-5 h-5 text-[#9B6FE8]" />
                                Season Tournaments
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {tournaments.length === 0 ? (
                                <p className="text-white/40 text-center py-4">No tournaments in this season yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {tournaments.map(t => (
                                        <SeasonTournamentCard key={t.id || t._id} tournament={t} />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
